"use client";
//#region import
import React, { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import NextImage from "@components/Image/NextImage";
import { findSiteNameWithoutCom, slugRegexMatcher } from "../../helpers/Helper";
import Sites from "../../constants/Sites";
import { useAppContext } from "@components/Provider";
import PrivacyCookiePopup from "./PrivacyCookiePopup";
// import ContentHeader from "@components/HeaderPage/ContentHeader";
import AlgoliaSearch from "@components/AlgoliaSearch/AlgoliaSearch";
import { useTabletDetection } from "../../helpers/useTabletDetection";

const NewsletterV2 = dynamic(() => import("@components/NewsletterV2/NewsletterV2"), { ssr: false });
const ContentMenuMobile = dynamic(() => import("@components/HeaderPage/ContentMenuMobile"), {
  ssr: false,
});
const CatalogMenuMobile = dynamic(() => import("@components/HeaderPage/CatalogMenuMobile"), {
  ssr: false,
});

import styles from "./Header.module.css";
import "./Header.css";
//#endregion

const Header = (props) => {
  const {
    data,
    siteId,
    subscriberCookie,
    isUserComingFromNL,
    wasLoggedInCookie,
    isMobile,
    isTablet: serverIsTablet,
    ...appProps
  } = useAppContext();

  // Enhanced client-side tablet detection using custom hook
  const { isTablet } = useTabletDetection(serverIsTablet, isMobile);

  let siteName = data?.SiteSettings?.site_name;
  const siteLogo = data?.SiteSettings?.primary_site_logo_svg
    ? data?.SiteSettings?.primary_site_logo_svg
    : data?.SiteSettings?.primary_site_logo;
  let slug = appProps?.router?.asPath && appProps?.router?.asPath.toString().replace(/%20/g, " ");
  //let originalSlug = appProps?.pageProps?.originalSlug;

  const [privacyCookie, setPrivacyCookie] = useState("shown");
  let hideHeader = props?.hideHeader || false;

  useEffect(() => {
    const value = localStorage.getItem("cookiePopup");
    setPrivacyCookie(value ? "shown" : "hidden");
  }, []);

  const [isOpen, setIsOpen] = useState(false);

  //To close the dropdown menu when click outside dropdown
  const node = useRef();
  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);

  const handleClick = (e) => {
    if (node.current.contains(e.target)) {
      if (e.target.type !== "text") {
        return;
      }
    }
  };

  //commented code to hide subscribe from header
  let subscribeClassName =
    isUserComingFromNL || wasLoggedInCookie
      ? siteId == Sites.OPENROADMEDIA
        ? [styles.vhidden, "noMargin", styles.subscribe].join(" ")
        : [styles.vhidden, styles.subscribe].join(" ")
      : siteId == Sites.OPENROADMEDIA
        ? ["noMargin", styles.subscribe].join(" ")
        : [styles.subscribe].join(" ");

  let primaryNewsletter =
    siteId === Sites.OPENROADMEDIA
      ? findSiteNameWithoutCom(Sites.EARLYBIRDBOOKS)
      : findSiteNameWithoutCom(siteId);
  let headerNewsletterProps = {
    ...props,
    primaryNewsletter: primaryNewsletter,
  };

  const renderHeaderNL = () => {
    let NL;

    //EBB reverted to old design per https://openroadmedia.atlassian.net/browse/ORMTECH-952
    let newsletterLink = siteId == Sites.EARLYBIRDBOOKS ? "/newsletters-2" : "/newsletters";
    // if slug regex matches signup, dont display NL for mobile or desktop
    if (slugRegexMatcher(slug, "signup")) {
      return null;
    }
    if (isMobile || isTablet) {
      // Special case: For tablets on OPENROADMEDIA, show subscribe button (not newsletter component)
      if (isTablet && siteId === Sites.OPENROADMEDIA) {
        // Check if there's a cookie or if slug matches NL
        if (
          subscriberCookie ||
          slugRegexMatcher(slug, "newsletters") ||
          slugRegexMatcher(slug, "newsletters-2")
        ) {
          NL = null;
        } else {
          NL = (
            <li className={subscribeClassName}>
              {/* <Link href={newsletterLink} as={newsletterLink}> */}
              <a href={newsletterLink}>Subscribe</a>
              {/* </Link> */}
            </li>
          );
        }
      } else {
        // Original logic for mobile and other tablets
        if (isOpen) {
          NL = (
            <li
              className={`${styles.show} ${styles.item} subscribeMenu`}
              onClick={() => setIsOpen(false)}
            >
              {/* <Link href={newsletterLink} as={newsletterLink}> */}
              <a href={newsletterLink}>Subscribe</a>
              {/* </Link> */}
            </li>
          );
        }
        // if its mobile/tablet and theres a cookie or if slug matches NL, dont display
        else if (
          subscriberCookie ||
          slugRegexMatcher(slug, "newsletters") ||
          slugRegexMatcher(slug, "newsletters-2")
        ) {
          NL = null;
        } else {
          NL = (
            <li className={[styles.subscribeNL].join(" ")}>
              <NewsletterV2
                isChild={true}
                Newsletter_Settings={data.SiteSettings.newsletter_settings}
                classNames={"headerNewsletter"}
                {...headerNewsletterProps}
                siteId={siteId}
                successClass={styles.success}
                msgBlockClass={styles.msgBlock}
                inlineStyles={{ button: { backgroundColor: "#333333" } }}
              />
            </li>
          );
        }
      }
    } else {
      NL = !hideHeader && (
        <li className={subscribeClassName}>
          {/* <Link href={newsletterLink} as={newsletterLink}> */}
          <a href={newsletterLink}>Subscribe</a>
          {/* </Link> */}
        </li>
      );
    }
    return NL;
  };

  const headerSectionClasses = [
    styles.headerSection,
    siteId == Sites.EARLYBIRDBOOKS ? styles.headerSectionEBBPadding : styles.headerSectionPadding,
    siteId == Sites.MURDER_MAYHEM || siteId == Sites.EXPLORETHEARCHIVE
      ? styles.headerSectionBlackColor
      : styles.headerSectionWhiteColor,
    (!isMobile && !isTablet && siteId !== Sites.OPENROADMEDIA) || isMobile
      ? styles.headerSectionOrder1
      : styles.headerSectionOrder2,
    siteId == Sites.MURDER_MAYHEM
      ? styles.headerSectionBlackColorError
      : styles.headerSectionWhiteColorError,
    isOpen ? styles.headerSectionHeight20 : styles.headerSectionHeight0,
    isOpen ? styles.headerSectionDNone : styles.headerSectionDBlock,
    !isOpen && isMobile && isTablet && styles.headerSectionMobileHeight0,
  ].join(" ");

  return (
    <React.Fragment>
      <style>
        {`
          @media (min-width: 712px) and (max-width: 712px) {
            .search {
              display: ${isOpen ? "block" : "none"};
            }
          }
        `}
      </style>
      {privacyCookie !== "shown" && (
        <PrivacyCookiePopup setPrivacyCookie={setPrivacyCookie} siteId={siteId} data={data} />
      )}
      <>
        <nav
          className={
            siteId == Sites.OPENROADMEDIA
              ? [headerSectionClasses, styles.catelogMenu, "navbar", "navbar-expand-lg"].join(" ")
              : [headerSectionClasses, "navbar", "navbar-expand-lg"].join(" ")
          }
          style={{
            background: data?.SiteSettings?.site_header?.headerColor,
            color: data?.SiteSettings?.site_header?.headerTextColor,
          }}
          ref={node}
        >
          <div className="container-fluid">
            <ul
              className={`${styles.menu} ${siteId === Sites.MURDER_MAYHEM || siteId === Sites.EXPLORETHEARCHIVE ? styles.menuBlackColor : styles.menuWhiteColor}`}
            >
              <li className={[styles.logo, "logo"].join(" ")}>
                <a href="/">
                  <NextImage
                    ImageUrl={siteLogo}
                    Title={siteName}
                    Alt_Text={siteName}
                    width={250}
                    height={30}
                    isLazyload={false}
                  />
                </a>
              </li>
              {siteId == Sites.OPENROADMEDIA ? (
                // <CatalogHeader
                //   siteId={siteId}
                //   data={data}
                //   slug={slug}
                //   isOpen={isOpen}
                //   setIsOpen={setIsOpen}
                //   isDDOpen={isDDOpen}
                //   setIsDDOpen={setIsDDOpen}
                //   dToggleClass={styles.dtoggle}
                //   subMenuClass={styles.subMenu}
                //   dactiveClass={styles.dactive}
                //   itemClass={styles.item}
                //   showClass={styles.show}
                //   dscrollClass={styles.dscroll}
                // />
                <>
                  {isMobile ? <CatalogMenuMobile slug={slug} /> : null}
                  {/* {isTablet && <Content slug={slug} />} */}
                </>
              ) : (
                !hideHeader && (
                  // <ContentHeader
                  //   siteId={siteId}
                  //   categories={categories}
                  //   navItems={navItems}
                  //   slug={slug}
                  //   isOpen={isOpen}
                  //   setIsOpen={setIsOpen}
                  //   data={data}
                  //   dToggleClass={styles.dtoggle}
                  //   subMenuClass={styles.subMenu}
                  //   dactiveClass={styles.dactive}
                  //   itemClass={styles.item}
                  //   showClass={styles.show}
                  // />
                  <>
                    {isMobile || isTablet ? (
                      <ContentMenuMobile isTablet={isTablet} isMobile={isMobile} slug={slug} />
                    ) : null}
                  </>
                )
              )}
              {!hideHeader && (
                <li
                  className={
                    isMobile || isTablet
                      ? isMobile || (isTablet && siteId !== Sites.OPENROADMEDIA)
                        ? isOpen
                          ? [styles.show, styles.item, styles.search, "search"].join(" ")
                          : ["hide", styles.search, "search"].join(" ")
                        : [styles.search, "search"].join(" ")
                      : [styles.search, "search"].join(" ")
                  }
                >
                  <div className="button-group">
                    <AlgoliaSearch setIsOpen={setIsOpen} />
                  </div>
                </li>
              )}
              {/* hide newsletter from header on mobile and tablet, if nonclosablemodal is checked */}
              {!isMobile && !isTablet && !props.isNonClosableModalChecked && renderHeaderNL()}
              {/* Show newsletter for tablet on OPENROADMEDIA */}
              {isTablet && siteId === Sites.OPENROADMEDIA && !props.isNonClosableModalChecked
                ? renderHeaderNL()
                : null}
              {/* {!hideHeader && (
              <li className={[styles.toggle, (!isMobile && !isTablet && siteId !== Sites.OPENROADMEDIA) ? styles.toggleDNone : styles.toggleDInBlock].join(" ")} onClick={toggle}>
                <a href="#">
                  <span className={`${isOpen ? `${styles.bars} ${styles.barsClose}` : styles.bars}
                    ${siteId === Sites.MURDER_MAYHEM || siteId === Sites.EXPLORETHEARCHIVE ? styles.barsBlackColor : styles.barsWhiteColor}`}></span>
                </a>
              </li>
            )} */}
            </ul>
          </div>
        </nav>
        <div
          style={{
            paddingTop: "5.5rem",
            listStyle: "none",
            margin: "auto 1.5rem",
          }}
        >
          {/* hide newsletter from header on mobile, if nonclosablemodal is checked */}
          {isMobile && !props.isNonClosableModalChecked ? renderHeaderNL() : null}
        </div>
      </>
    </React.Fragment>
  );
};

export default Header;
